import { EventEmitter, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Component, Input, OnInit } from '@angular/core';
import { CompanyDTO } from 'src/AngularJS/Admin/Company/company.model';
import { SsoProvider, SsoSettings } from 'src/app/admin/company-settings/company-edit-sso/company-edit-sso.model';
import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CompanyEditSSOService } from 'src/app/admin/company-settings/company-edit-sso/company-edit-sso.service';
@Component({
    selector: 'cv-company-edit-sso',
    templateUrl: './company-edit-sso.component.html',
    styleUrls: ['./company-edit-sso.component.scss']
})
export class CompanyEditSsoComponent implements OnInit, OnChanges {
    @Input() ssoObject: CompanyDTO | SsoSettings;
    @Input() editing: boolean = false;
    @Output() ssoObjectChange: EventEmitter<CompanyDTO | SsoSettings> = new EventEmitter<CompanyDTO | SsoSettings>();
    @Output() validationErrors: EventEmitter<string[]> = new EventEmitter<string[]>();

    public localization: Map<string, string>;
    public settings: SsoSettings = null;
    public options: Map<string, SsoProvider>;
    public providers: Array<string> = [];
    public protocols: Array<string> = [];
    public encryptions: Array<string> = [];
    public transportAlgorithms: Array<string> = [];
    public samlBindings: Array<string> = [];
    public samlDownloadDisabled: boolean = false;

    private _ssoObject: CompanyDTO | SsoSettings;
    private isCompanyDTO: boolean = false;
    private settingsSubject = new Subject<SsoSettings>();

    constructor(private service: CompanyEditSSOService, private locService: CVLocalizationService) { }

    async ngOnInit() {
        this.isCompanyDTO = this.ssoObject instanceof CompanyDTO;

        this.localization = await this.locService.GetSection("SSO_Settings") ?? new Map<string, string>();

        this.options = await this.service.getSsoOptions();

        this.providers = Array.from(this.options.keys());

        this.settings = cloneDeep(this.ssoObject as SsoSettings);
        this._ssoObject = cloneDeep(this.ssoObject);

        if (this.settings.EnableSSO)
            this.updateProvider();

        this.settingsSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(this.emit.bind(this));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.localization && this.options && this.providers && changes.ssoObject) {
            this.isCompanyDTO = this.ssoObject instanceof CompanyDTO;
            this.settings = cloneDeep(this.ssoObject as SsoSettings);
            this._ssoObject = cloneDeep(this.ssoObject);
            this.updateProvider(false);
        }
    }

    public onUpdate() {
        this.validate();
        this.settingsSubject.next(cloneDeep(this.settings));
    }

    public toggleSSO(): void {
        if (this.settings.EnableSSO)
            this.ensureDefaultValues();
        else
            this.clearSettings();

        this.onUpdate();
    }

    public updateProvider(canEmit:boolean = true): void {
        this.protocols = Array.from(this.options.get(this.settings.SSOProvider)?.Protocols?.keys() ?? []);

        if (!this.protocols.includes(this.settings.SSOProtocol))
            this.settings.SSOProtocol = this.protocols[0];

        if (!!this.settings.SSOProtocol)
            this.updateProtocol(canEmit);
        else
            this.clearSettings();
    }

    public updateProtocol(canEmit:boolean = true): void {
        const protocol = this.options.
            get(this.settings.SSOProvider).Protocols.
            get(this.settings.SSOProtocol);
        this.encryptions = protocol.Encryptions;
        this.transportAlgorithms = protocol.TransportAlgorithms;
        this.samlBindings = protocol.SamlBindings ?? [];

        this.clearSettings();

        if (!this.encryptions.includes(this.settings.SSOEncryptionMethod))
            this.settings.SSOEncryptionMethod = this.encryptions[0];

        if (!this.transportAlgorithms.includes(this.settings.SSOTransportAlgorithm))
            this.settings.SSOTransportAlgorithm = ""; // Not implemented yet- this.transportAlgorithms[0];

        if (!!this.samlBindings && !this.samlBindings.includes(this.settings.SSOSamlBinding))
            this.settings.SSOSamlBinding = (!this.samlBindings || this.samlBindings.length == 0) ? "" : this.samlBindings[0];

        if(canEmit)
            this.onUpdate();
    }

    public hasClientSecret(): boolean {
        return this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasClientSecret ?? false;
    }

    public hasX509Certificate(): boolean {
        return this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasX509Certificate ?? false;
    }

    public hasIdPID(): boolean {
        return this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasIdPId ?? false;
    }

    public hasSamlBindings(): boolean {
        return this.settings?.SSOProvider == 'Advanced' && this.settings?.SSOProtocol == 'SAML'
            && this.samlBindings?.length > 0;
    }

    public hasMetaDL(): boolean {
        return (this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasMetaDL
            && this.settings?.SSOHasMetadata) ?? false;
    }

    public hasSigningCertDL(): boolean {
        return (this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasSigningCertDL
            && this.settings?.SSOHasSigningKeys);
    }

    public hasEncryptionCertDL(): boolean {
        return this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasEncryptionCertDL
            && this.settings?.SSOHasEncryptionKeys;
    }

    public hasSAMLDL(): boolean {
        return this.hasMetaDL() || this.hasSigningCertDL() || this.hasEncryptionCertDL();
    }

    public hasWantSignedAssertion(): boolean {
        return this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasWantSignedAssertion
            ?? false;
    }

    public hasSignAuthnRequests(): boolean {
        return this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasSignAuthnRequests
            ?? false;
    }

    public hasRollSigningKeys(): boolean {
        return this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasRollSigningKeys
            && this.settings?.SSOHasSigningKeys;
    }

    public hasRollEncryptionKeys(): boolean {
        return this.options?.get(this.settings?.SSOProvider)?.Protocols?.get(this.settings?.SSOProtocol)?.HasRollEncryptionKeys
            && this.settings?.SSOHasEncryptionKeys;
    }

    public download(index: number = 0) {
        if (this.samlDownloadDisabled)
            return;
        let url: string;
        switch (index) {
            case 0:
                url = "/sso-auth/saml-metadata";
                break;
            case 1:
                url = "/sso-auth/saml-cert/signing";
                break;
            case 2:
                url = "/sso-auth/saml-cert/encryption";
                break;
        }
        window.open(url, "_blank");
    }

    private ensureDefaultValues(): void {
        if (!this.settings.SSOProvider || this.settings.SSOProvider.length == 0) {
            this.settings.SSOProvider = this.options.keys().next().value; // choosing the first provider as a default
            this.updateProvider();
        }
        const providerOptions = this.options.get(this.settings.SSOProvider);
        if (!providerOptions) {
            console.error("Unexpected Company Provider specified.");
            return;
        }

        if (!this.settings.SSOProtocol || this.settings.SSOProtocol.length == 0) {
            this.settings.SSOProtocol = providerOptions.Protocols.keys().next().value; // first protocol as default
        }

        const protocol = providerOptions.Protocols.get(this.settings.SSOProtocol);
        if (!protocol) {
            console.error("Unexpected Company Protocol specified.");
            return;
        }

        if (!this.settings.SSOEncryptionMethod || this.settings.SSOEncryptionMethod.length == 0) {
            this.settings.SSOEncryptionMethod = protocol.Encryptions[0];
        }

        if (!this.settings.SSOTransportAlgorithm || this.settings.SSOTransportAlgorithm.length == 0)
            this.settings.SSOTransportAlgorithm = "";// Not implemented yet- protocol.TransportAlgorithms[0];

        if (this.settings.SSOProvider == "Advanced" && this.settings.SSOProtocol == "SAML") {
            if (!this.settings.SSOSamlBinding || this.settings.SSOSamlBinding.length == 0)
                this.settings.SSOSamlBinding = protocol.SamlBindings[1];
        }
    }

    private clearSettings() {
        if (this.settings.EnableSSO == false) {
            this.settings.SSOProvider = "";
            this.settings.SSOProtocol = "";
            this.settings.SSOClientID = "";
            this.settings.SSOClientSecret = "";
            this.settings.SSOEncryptionMethod = "";
            this.settings.SSOTransportAlgorithm = "";
            this.settings.SSOIdentityProviderURL = "";
            this.settings.SSOX509Certificate = "";
            this.settings.SSOIdPId = "";
            this.settings.SSOSamlBinding = "";
            this.settings.SSOSignAuthnRequests = false;
            this.settings.SSOSignedAssertions = false;
            this.settings.SSORollEncryption = false;
            this.settings.SSORollSigning = false;
            this.settings.SSOHasMetadata = false;
            this.settings.SSOHasSigningKeys = false;
            this.settings.SSOHasEncryptionKeys = false;
        }

        if (this.settings.SSOProvider == "Okta") {
            // Settings not used by Okta
            this.settings.SSOSamlBinding = "";
            this.settings.SSOTransportAlgorithm = "";
            this.settings.SSOSignAuthnRequests = false;
            this.settings.SSOSignedAssertions = false;
            this.settings.SSORollEncryption = false;
            this.settings.SSORollSigning = false;
            this.settings.SSOHasMetadata = false;
            this.settings.SSOHasSigningKeys = false;
            this.settings.SSOHasEncryptionKeys = false;

            
            if (this.settings.SSOProtocol == "OpenID") {
                // Settings not used by Okta OIDC
                this.settings.SSOX509Certificate = "";
                this.settings.SSOIdPId = "";
            }
        }

        // Settings not used Okta || Advanced SAML
        if (this.settings.SSOProtocol == "SAML") 
            this.settings.SSOClientSecret = "";
        


    }

    private emit(settings: SsoSettings) {
        //if (this.settings.EnableSSO) {
        //    if (!this.validate())
        //        return;
        //}
        this.validate();

        this.settings.SSOX509Certificate = this.service.cleanSecurityCertificate(this.settings.SSOX509Certificate)

        // Update ssoObject so we can emit it
        if (this.isCompanyDTO) {
            this._ssoObject.EnableSSO = this.settings.EnableSSO;
            this._ssoObject.SSOClientID = this.settings.SSOClientID;
            this._ssoObject.SSOClientSecret = this.settings.SSOClientSecret;
            this._ssoObject.SSOEncryptionMethod = this.settings.SSOEncryptionMethod;
            this._ssoObject.SSOIdentityProviderURL = this.settings.SSOIdentityProviderURL;
            this._ssoObject.SSOProtocol = this.settings.SSOProtocol;
            this._ssoObject.SSOProvider = this.settings.SSOProvider;
            this._ssoObject.SSOTransportAlgorithm = this.settings.SSOTransportAlgorithm;
            this._ssoObject.SSOX509Certificate = this.settings.SSOX509Certificate;
            this._ssoObject.SSOIdPId = this.settings.SSOIdPId;
            this._ssoObject.SSOSamlBinding = this.settings.SSOSamlBinding;
            this._ssoObject.SSOSignedAssertions = this.settings.SSOSignedAssertions;
            this._ssoObject.SSOSignAuthnRequests = this.settings.SSOSignAuthnRequests;
            this._ssoObject.SSORollSigning = this.settings.SSORollSigning;
            this._ssoObject.SSORollEncryption = this.settings.SSORollEncryption;
        }
        else
            this._ssoObject = this.settings;

        this.ssoObjectChange.emit(this._ssoObject)
    }

    private validate() {
        let errors: string[] = [];

        if (!this.settings.EnableSSO) {
            if (this.settings.SSOClientID != "")
                errors.push("ClientID should be empty.");
            if (this.settings.SSOClientSecret != "")
                errors.push("Client Secret should be empty.");
            if (this.settings.SSOEncryptionMethod != "")
                errors.push("Encryption Method should be empty.");
            if (this.settings.SSOIdentityProviderURL != "")
                errors.push("IdP URL should be empty.");
            if (this.settings.SSOProtocol != "")
                errors.push("Protocol should be empty.");
            if (this.settings.SSOProvider != "")
                errors.push("Provider should be empty.");
            if (this.settings.SSOTransportAlgorithm != "")
                errors.push("Transport Algorithm should be empty.");
            if (this.settings.SSOX509Certificate != "")
                errors.push("X.509 Certificate should be empty.");
            if (this.settings.SSOIdPId != "")
                errors.push("IdP ID should be empty.");
            if (this.settings.SSOSamlBinding != "")
                errors.push("Binding should be empty.");
            if (this.settings.SSOSignedAssertions != false)
                errors.push("Signed Assertions should be false.");
            if (this.settings.SSOSignAuthnRequests != false)
                errors.push("Sign Authn Requests should be false.");
            if (this.settings.SSORollSigning != false)
                errors.push("Roll Signing Certificate should be false.");
            if (this.settings.SSORollEncryption != false)
                errors.push("Roll Encryption Certificate should be false.");
            if (this.settings.SSOHasMetadata != false)
                errors.push("Has Metadata should be false.");
            if (this.settings.SSOHasSigningKeys != false)
                errors.push("Has Signing Keys should be false.");
            if (this.settings.SSOHasEncryptionKeys != false)
                errors.push("Has Encryption Keys should be false.");
        }
        else {
            if (this.settings.SSOProtocol == "SAML") {
                if (this.settings.SSOProvider == "Advanced") {
                    if (this.settings.SSOIdPId.trim().length == 0)
                        errors.push("Invalid IdP ID.");

                    if (this.settings.SSOSamlBinding.trim().length == 0)
                        errors.push("Invalid SAML Binding.");
                }
                else if (this.settings.SSOProvider != "Okta")
                    errors.push("Invalid SSO Provider.");
                // Okta doesn't have any unique SAML settings

                if (this.settings.SSOX509Certificate.trim().length == 0)
                    errors.push("Invalid x.509 Certificate.");
            }
            else if (this.settings.SSOProtocol == "OpenID") {
                if (this.settings.SSOClientSecret.trim().length == 0)
                    errors.push("Invalid Client Secret.");
            }
            else
                errors.push("Invalid SSO Protocol.");

            if (this.settings.SSOIdentityProviderURL.trim().length == 0 || !this.settings.SSOIdentityProviderURL.startsWith("https://"))
                errors.push("Invalid IdP URL.");

            if (this.settings.SSOClientID.trim().length == 0)
                errors.push("Invalid Client ID.");
        }

        this.validationErrors.emit(errors);

        if (errors.length == 0)
            return true;
        return false;
    }
}
